import type { FimMode } from '@bartizo/shared';

/**
 * What each setting is, in words (knowledge doc section 11: "Settings are edited
 * only through **structured fields** (lists, numbers, switches), never raw config
 * text").
 *
 * A table rather than a form per module. The alternative is four screens of JSX
 * that restate every bound the shared schema already carries and then drift from
 * it; this keeps the part that is genuinely per-field — what it is called, what it
 * is for, what unit it is in — and lets one renderer do the rest.
 *
 * The bounds here are shown to a person, not enforced here: `SettingsPatch` is what
 * refuses a bad value, at the API, where it cannot be worked around by anything
 * that skips this screen.
 */

export type FieldSpec =
  | { key: string; label: string; hint?: string; kind: 'switch' }
  | {
      key: string;
      label: string;
      hint?: string;
      kind: 'number';
      min: number;
      max: number;
      /** For a value that need not be whole; 1 otherwise. */
      step?: number;
      /** Shown beside the input: `s`, `%`. Units on the wire are section 5's. */
      unit?: string;
    }
  | { key: string; label: string; hint?: string; kind: 'strings'; placeholder?: string }
  | { key: string; label: string; hint?: string; kind: 'numbers'; placeholder?: string }
  | { key: string; label: string; hint?: string; kind: 'targets' }
  | { key: string; label: string; hint?: string; kind: 'fim_paths' }
  | {
      key: string;
      label: string;
      hint?: string;
      kind: 'choice';
      /** The first is what a newly overridden field starts on. */
      options: readonly { value: string; label: string }[];
    };

/** Section 11's drift (P8.3): what to do about a Bartizo file somebody changed. */
const ON_DRIFT: FieldSpec = {
  key: 'on_drift',
  label: 'When a file Bartizo writes is changed by hand',
  hint: 'Bartizo looks every hour. Put back, it is rewritten and the change is reported. Only alert, it is left, the change is reported once, and the server shows as drifted until the file matches the settings again.',
  kind: 'choice',
  options: [
    { value: 'put_back', label: 'Put it back' },
    { value: 'alert', label: 'Only alert' },
  ],
};

/** Section 8.2's three modes, in words (`FimPathListEditor`, and inherited values). */
export const MODE_LABELS: Readonly<Record<FimMode, string>> = {
  scheduled: 'On a schedule',
  realtime: 'Real time',
  whodata: 'Real time, and who did it',
};

export interface ModuleSpec {
  key: string;
  title: string;
  description: string;
  fields: FieldSpec[];
}

/** Section 8.3. */
const disk: ModuleSpec = {
  key: 'disk',
  title: 'Disk',
  description: 'Every server runs this one: section 7 puts it in the base role.',
  fields: [
    { key: 'enabled', label: 'Enabled', kind: 'switch' },
    {
      key: 'interval_seconds',
      label: 'Check every',
      kind: 'number',
      min: 30,
      max: 3600,
      unit: 's',
    },
    { key: 'warn_pct', label: 'Warn above', kind: 'number', min: 1, max: 100, unit: '%' },
    { key: 'high_pct', label: 'High above', kind: 'number', min: 1, max: 100, unit: '%' },
    {
      key: 'critical_pct',
      label: 'Critical above',
      hint: 'The three thresholds have to increase in that order.',
      kind: 'number',
      min: 1,
      max: 100,
      unit: '%',
    },
    {
      key: 'cooldown_seconds',
      label: 'Quiet period after an alert',
      hint: 'Stops a mount hovering on a threshold from raising an event every check.',
      kind: 'number',
      min: 60,
      max: 86_400,
      unit: 's',
    },
    {
      key: 'mounts',
      label: 'Only these mounts',
      hint: 'Leave empty to watch every real mount. One absolute path per line.',
      kind: 'strings',
      placeholder: '/\n/srv',
    },
    {
      key: 'top_folders_interval_seconds',
      label: 'Largest-folders scan every',
      hint: 'A du pass over the filesystem, which is the expensive one.',
      kind: 'number',
      min: 600,
      max: 86_400,
      unit: 's',
    },
  ],
};

/** Section 8.2. */
const wazuhFim: ModuleSpec = {
  key: 'wazuh_fim',
  title: 'File integrity',
  description: 'Wazuh FIM: which paths are watched and how loudly.',
  fields: [
    { key: 'enabled', label: 'Enabled', kind: 'switch' },
    {
      key: 'min_level',
      label: 'Ignore alerts below level',
      hint: "Wazuh's own scale. 5 is its default and drops most noise.",
      kind: 'number',
      min: 0,
      max: 15,
    },
    {
      key: 'paths',
      label: 'Watched paths',
      hint: "Added to what each server's roles and the wider layers watch; naming one of those here changes only its mode. A change restarts Wazuh, and a restart does not report files changed under a scheduled path since its last scan.",
      kind: 'fim_paths',
    },
    {
      key: 'ignore',
      label: 'Ignore these paths',
      hint: 'One per line.',
      kind: 'strings',
      placeholder: '/var/www/cache',
    },
    {
      key: 'scan_interval_seconds',
      label: 'Scan scheduled paths every',
      hint: "Wazuh cannot be asked for a scan, so this is how a change under a scheduled path is found sooner. Not set, Wazuh's own applies: every 12 hours (43200) where the installer set it up. Each scan reads every file in those paths, and a change restarts Wazuh.",
      kind: 'number',
      min: 3600,
      max: 86_400,
      unit: 's',
    },
    ON_DRIFT,
  ],
};

/** Section 8.1. */
const crowdsec: ModuleSpec = {
  key: 'crowdsec',
  title: 'CrowdSec',
  description: 'Brute force and IP blocking. Bartizo configures it and reads its decisions.',
  fields: [
    { key: 'enabled', label: 'Enabled', kind: 'switch' },
    {
      key: 'poll_seconds',
      label: 'Read alerts and decisions every',
      hint: 'There is a floor: repeated unfiltered queries have pinned LAPI at 100% CPU.',
      kind: 'number',
      min: 15,
      max: 600,
      unit: 's',
    },
    {
      key: 'collections',
      label: 'Collections',
      hint: 'One author/name per line. Adding here adds to what wider layers set.',
      kind: 'strings',
      placeholder: 'crowdsecurity/nginx',
    },
  ],
};

/** Section 8.4. */
const dbSize: ModuleSpec = {
  key: 'db_size',
  title: 'Database size',
  description: 'Off unless a server has the database role.',
  fields: [
    { key: 'enabled', label: 'Enabled', kind: 'switch' },
    {
      key: 'interval_seconds',
      label: 'Measure every',
      kind: 'number',
      min: 300,
      max: 86_400,
      unit: 's',
    },
    {
      key: 'growth_alert_pct_24h',
      label: 'Alert on growth over',
      kind: 'number',
      min: 1,
      max: 10_000,
      unit: '% / 24h',
    },
    {
      key: 'targets',
      label: 'Databases',
      hint: 'The health and exposure checks read the same list. A database named in a wider layer is added to, not replaced, unless this layer names it too.',
      kind: 'targets',
    },
  ],
};

/** Section 8.5. Reads the databases named under Database size. */
const dbHealth: ModuleSpec = {
  key: 'db_health',
  title: 'Database health',
  description:
    'Connections, failed logins, replication and slow queries. Off unless a server has the database role; it reads the databases listed under Database size.',
  fields: [
    { key: 'enabled', label: 'Enabled', kind: 'switch' },
    {
      key: 'interval_seconds',
      label: 'Check every',
      kind: 'number',
      min: 60,
      max: 300,
      unit: 's',
    },
    {
      key: 'long_query_seconds',
      label: 'A query is slow after',
      hint: 'Counted, not reported one by one: the number running this long right now.',
      kind: 'number',
      min: 1,
      max: 86_400,
      unit: 's',
    },
    {
      key: 'connections_alert_pct',
      label: 'Alert when connections reach',
      hint: 'As a share of the maximum the database allows. Said again only once they come well back down.',
      kind: 'number',
      min: 1,
      max: 100,
      unit: '%',
    },
    {
      key: 'replication_lag_alert_seconds',
      label: 'Alert when a replica is behind by',
      kind: 'number',
      min: 1,
      max: 604_800,
      unit: 's',
    },
    {
      key: 'failed_logins_alert_count',
      label: 'Alert on failed logins in one check',
      hint: 'Only when also five times the usual: some databases count a load balancer’s health check as a failed login.',
      kind: 'number',
      min: 1,
      max: 1_000_000,
    },
  ],
};

/** Section 8.6. */
const dbExposure: ModuleSpec = {
  key: 'db_exposure',
  title: 'Database exposure',
  description:
    'Database ports open to the internet, databases that need no password, and releases past their support. Off unless a server has the database role.',
  fields: [
    { key: 'enabled', label: 'Enabled', kind: 'switch' },
    {
      key: 'interval_seconds',
      label: 'Check every',
      kind: 'number',
      min: 300,
      max: 86_400,
      unit: 's',
    },
    {
      key: 'ports',
      label: 'Also watch these ports',
      hint: 'Besides 3306, 33060, 5432, 27017 and 6379, and each database’s own. One per line; adding here adds to what wider layers set.',
      kind: 'numbers',
      placeholder: '3307',
    },
  ],
};

/** Section 8.8 (P5.5). */
const webEnv: ModuleSpec = {
  key: 'web_env',
  title: 'Web environment detection',
  description:
    'Finds Nginx and Apache, and whether Cloudflare or a load balancer is in front, and keeps those proxies from being banned. Read-only: it proposes, and an admin confirms. Off unless a server has the web role.',
  fields: [
    { key: 'enabled', label: 'Enabled', kind: 'switch' },
    {
      key: 'inspect_interval_seconds',
      label: 'Look again every',
      hint: 'Section 8.8 says every six hours.',
      kind: 'number',
      min: 3600,
      max: 86_400,
      unit: 's',
    },
    {
      key: 'sample_seconds',
      label: 'Watch connections for',
      hint: 'How long each look samples who connects to the web ports. A quiet site may need longer.',
      kind: 'number',
      min: 60,
      max: 3600,
      unit: 's',
    },
    { ...ON_DRIFT, label: "When Bartizo's Nginx or Apache files are changed by hand" },
    {
      key: 'bouncer',
      label: 'Web bouncer (Nginx)',
      hint: 'CrowdSec’s bouncer inside Nginx: bans reach visitors even behind Cloudflare or a load balancer. Installed the first time it is switched on; switched off, it stays installed and lets everything through.',
      kind: 'switch',
    },
    {
      key: 'appsec',
      label: 'AppSec web firewall',
      hint: 'Needs the web bouncer. Watch reports what its rules (known exploits, secret files) match and blocks nothing; Block refuses those requests with 403.',
      kind: 'choice',
      options: [
        { value: 'off', label: 'Off' },
        { value: 'watch', label: 'Watch only' },
        { value: 'block', label: 'Block' },
      ],
    },
    {
      key: 'challenge',
      label: 'Challenge (captcha)',
      hint: 'Lets a ban be a captcha instead. Needs the web bouncer and the captcha keys in Integrations.',
      kind: 'switch',
    },
  ],
};

/** Section 8.7 (P6.3). */
const webTraffic: ModuleSpec = {
  key: 'web_traffic',
  title: 'Web traffic',
  description:
    'Reads Bartizo’s access log on the server and sends a summary each minute — who visited, which bots, which paths — never the log lines themselves. The log exists once the web environment is confirmed on a server’s page. Off unless a server has the web role.',
  fields: [
    { key: 'enabled', label: 'Enabled', kind: 'switch' },
    {
      key: 'verify_concurrency',
      label: 'Bot checks at once',
      hint: 'How many claims to be Googlebot and the like are checked by DNS at the same time. They are also held to five a second.',
      kind: 'number',
      min: 1,
      max: 20,
    },
    {
      key: 'spike_factor',
      label: 'Spike above',
      hint: 'A minute with at least 100 requests and this many times the last hour’s average raises a traffic spike event, at most one an hour.',
      kind: 'number',
      min: 1.5,
      max: 100,
      step: 0.5,
      unit: '×',
    },
  ],
};

/** Processor, load and memory (P11.10): measured and charted, never alerted on. */
const system: ModuleSpec = {
  key: 'system',
  title: 'Processor and memory',
  description: 'Charts on the server page; no alerts.',
  fields: [
    { key: 'enabled', label: 'Enabled', kind: 'switch' },
    {
      key: 'interval_seconds',
      label: 'Check every',
      kind: 'number',
      min: 15,
      max: 3600,
      unit: 's',
    },
  ],
};

export const MODULE_SPECS: readonly ModuleSpec[] = [
  disk,
  system,
  wazuhFim,
  crowdsec,
  dbSize,
  dbHealth,
  dbExposure,
  webEnv,
  webTraffic,
];

/** Not a module: it applies to the server rather than to one check. */
export const ALLOWLIST_FIELD: FieldSpec = {
  key: 'allowlist',
  label: 'Never ban these ranges',
  hint: 'CIDR, one per line, like 203.0.113.0/24, no wider than /8 (or /16 for IPv6). Bans are refused for these, and each server puts them on CrowdSec’s own allowlist. Adding here adds to what wider layers set; it cannot take one away.',
  kind: 'strings',
  placeholder: '203.0.113.0/24',
};

/**
 * Every setting as a dotted path, for the "inherited" column.
 *
 * Flat rather than nested because that is how a field is addressed in this screen,
 * in a diff, and in an audit entry — one spelling for one setting, everywhere.
 */
export function flattenSettings(value: unknown, prefix = ''): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return prefix === '' ? {} : { [prefix]: value };
  }

  const flat: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    Object.assign(flat, flattenSettings(child, prefix === '' ? key : `${prefix}.${key}`));
  }
  return flat;
}
